import {
  getLevel2BackendKeys,
  getParentBackendKey,
  isBoardGovernancePageId,
  isBoardGovernanceScopeKey,
  isLevel1BackendKey,
  resolveBackendKey,
  STUDIO_SCOPE_BACKEND_KEY,
} from '@sg/_config/permissionMapping';
import {
  getFlatPermission,
  isPermissionReady,
} from '@sg/_config/permissionStore';
import type { IFlatPermission } from '@sg/types/permission';

/**
 * Mgmt PC permission rules
 *
 * Permissions come from the backend API (see permissionStore). Each pageId maps to a
 * backend key in permissionMapping.ts. Nothing is granted until permissions finish loading.
 *
 * Tabs & pages (parent-first):
 * We always check the parent first. If the parent has view access (readable), the child
 * gets view access too — we do not look at the child. Same for edit (writable).
 * If the parent does not have access, we then check that specific child on its own.
 * One sibling having access does not grant access to another sibling.
 *
 * Top-level module tabs (Business / Studio / Publishing):
 * Same parent-first idea, but if the L1 parent has no access, we still show the module
 * when any L2 page under it has access — so users with partial access can enter the module
 * and only see the tabs they are allowed to.
 *
 * Board Governance is a Level-2 page with its own node (studio.board_governance),
 * registered through the same page permission flow as the other Studio tabs. It does not
 * inherit access from the Studio parent: readable/writeable come directly from its own node.
 * Which studios/meetings appear is further limited by studio_ids on that node when present;
 * omitted means all studios (see getStudiosPermission).
 */

/** 前端页面元数据（不参与权限判定，用户访问由后端 API 控制） */
interface IPageMetadata {
  pageId: string;
  /** 管理员，仅用于没权限时显示的账号名 */
  admins?: string[];
  /** 当没权限或没数据时要隐藏模块 */
  noAccessHide?: boolean;
}

interface IPagePermission {
  /** 是否有查看权限 */
  view: boolean;
  /** 是否有编辑权限 */
  edit: boolean;
}

const DENIED_PERMISSION: IPagePermission = { view: false, edit: false };

/** NoAuthPage contact for all management tabs */
export const DEFAULT_PAGE_ADMINS = ['irenejliang'];

/**
 * 页面元数据注册表
 * - admins：NoAuthPage 联系人
 * - noAccessHide：无权限时隐藏模块（非显示无权限提示）
 * 用户访问权限由后端 /api/v1/mgmt_pc/permission/user_permission 控制
 */
export const PAGE_PERMISSION: IPageMetadata[] = [
  // Business Management
  { pageId: 'business', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'business_executiveSummary', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'business_totalGrossRevenue_notes' },
  { pageId: 'business_totalProfit_notes' },
  { pageId: 'business_weeklyProjectHighlight' },
  { pageId: 'business_overallFinancials', admins: DEFAULT_PAGE_ADMINS },
  // Studio Management
  { pageId: 'studio', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'studio_portfolio', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'studio_overview_notes' },
  { pageId: 'studio_overview_grossRevenueTrend' },
  { pageId: 'studio_overview_pipelineCalendar' },
  { pageId: 'studio_studios', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'studio_studios_Appendix&Notes' },
  { pageId: 'studio_studios_pipelineCalendar' },
  { pageId: 'studio_liveTitles', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'studio_liveTitles_notes' },
  { pageId: 'studio_liveTitles_liveVisualizationAll' },
  { pageId: 'studio_inDevProjects', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'studio_inDevProjects_headcount' },
  { pageId: 'studio_inDevProjects_lifeCycleFinancialsForecast' },
  { pageId: 'studio_inDevProjects_GRManagement' },
  { pageId: 'studio_inDevProjects_notes' },
  { pageId: 'studio_inDevProjects_inDevProjectsAll' },
  // Board Governance is currently a Level-2 tab.
  { pageId: 'studio_boardGovernance', admins: DEFAULT_PAGE_ADMINS },
  // Keep these Level-3 page registrations ready if embedded modules return later:
  // { pageId: 'studio_overview_boardGovernance' },
  // { pageId: 'studio_studios_boardGovernance' },
  // Publishing Management
  { pageId: 'publishing', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'publishing_overview', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'publishing_overview_grossRevenueByProject' },
  { pageId: 'publishing_overview_decisionPointsList' },
  { pageId: 'publishing_overview_notes', noAccessHide: true },
  { pageId: 'publishing_mktcost', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'publishing_mktcost_mktCostByProject' },
  { pageId: 'publishing_mktcost_mktBudgetApplicationCalendar' },
  { pageId: 'publishing_mktcost_notes', noAccessHide: true },
  { pageId: 'publishing_headcount', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'publishing_headcount_notes' },
  { pageId: 'publishing_headcount_projectHeadcount' },
  { pageId: 'publishing_headcount_Appendix&Notes', noAccessHide: true },
  { pageId: 'publishing_project', admins: DEFAULT_PAGE_ADMINS },
  { pageId: 'publishing_project_publishingReviewUpdates' },
  { pageId: 'publishing_project_Appendix&Notes', noAccessHide: true },
];

const getPageMetadata = (pageId: string): IPageMetadata | undefined => (
  PAGE_PERMISSION.find(item => item.pageId === pageId)
);

const toPagePermission = (permission?: IFlatPermission): IPagePermission => ({
  view: !!permission?.readable,
  edit: !!permission?.writable,
});

/**
 * Level-1 tab: parent readable/writable first; otherwise any L2 child grants access.
 */
const resolveLevel1Permission = (backendKey: string): IPagePermission => {
  const parent = getFlatPermission(backendKey);
  if (!parent) {
    return DENIED_PERMISSION;
  }

  const childKeys = getLevel2BackendKeys(backendKey);
  const anyChildReadable = childKeys.some(
    key => getFlatPermission(key)?.readable,
  );
  const anyChildWritable = childKeys.some(
    key => getFlatPermission(key)?.writable,
  );

  return {
    view: parent.readable || anyChildReadable,
    edit: parent.writable || anyChildWritable,
  };
};

/**
 * Parent-first inheritance for L2/L3 nodes.
 * Uses the direct parent's own readable/writeable — not L1 sibling aggregation.
 * Example: business.readable=0 → each L2 page falls back to its own readable flag.
 */
const resolveInheritedPermission = (backendKey: string): IPagePermission => {
  if (isLevel1BackendKey(backendKey)) {
    return resolveLevel1Permission(backendKey);
  }

  const own = getFlatPermission(backendKey);

  // Board Governance scope node: own readable/writeable only (no studio parent inherit).
  if (isBoardGovernanceScopeKey(backendKey)) {
    return toPagePermission(own);
  }

  const parentKey = getParentBackendKey(backendKey);

  if (!parentKey) {
    return toPagePermission(own);
  }

  // L2 pages inherit from L1's own flags only (not "any child readable" aggregation).
  const parentFlat = getFlatPermission(parentKey);

  return {
    view: !!parentFlat?.readable || !!own?.readable,
    edit: !!parentFlat?.writable || !!own?.writable,
  };
};

const getBackendPagePermission = (pageId: string): IPagePermission => {
  const backendKey = resolveBackendKey(pageId);
  if (!backendKey) {
    return DENIED_PERMISSION;
  }

  return resolveInheritedPermission(backendKey);
};

export const getPagePermission = (pageId: string): IPagePermission => {
  // API 未就绪时拒绝访问，避免闪现本地默认权限
  if (!isPermissionReady()) {
    return DENIED_PERMISSION;
  }

  return getBackendPagePermission(pageId);
};

export const getFormattedTabList = (tabList: any[] = []) => tabList.map(item => {
  const permission = getPagePermission(item.key);
  const pageMetadata = getPageMetadata(item.key);

  return {
    ...item,
    // 如果没有编辑权限，那就返回查看权限
    // 如果有编辑权限，就直接使用编辑权限覆盖查看权限
    hasAuth: permission.view,
    permission,
    admins: pageMetadata?.admins ?? DEFAULT_PAGE_ADMINS,
  };
});

export const getButtonPermission = (key: string): boolean => (
  getPagePermission(key).edit
);

export const getStudiosPermission = (pageId: string, studioId: string): boolean => {
  if (!pageId || !isBoardGovernancePageId(pageId)) {
    return true;
  }

  if (!isPermissionReady()) {
    return false;
  }

  const boardGovernancePermission = getFlatPermission(STUDIO_SCOPE_BACKEND_KEY);
  if (!boardGovernancePermission?.readable) {
    return false;
  }

  const normalizedStudioId = studioId?.trim();
  // 空 studioId 表示判断用户是否有任意 Board Governance 访问权限
  if (!normalizedStudioId) {
    return true;
  }

  const { studioIds } = boardGovernancePermission ?? {};
  // 未返回 studio_ids 或为空数组均表示全部 studio 可见
  if (studioIds === undefined || studioIds.length === 0) {
    return true;
  }

  return studioIds.includes(normalizedStudioId);
};

export const getAppendixNotesPermission = (pageId: string): boolean => {
  if (!pageId) {
    return false;
  }

  return !!getPageMetadata(pageId)?.noAccessHide;
};
